// React context provider that owns the draft state (via useDraftChannel)
// and the player pool fetch. Exposes both via context so DraftRoom and
// Board can consume them without prop drilling.

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from '../lib/api'
import { getTeamToken } from '../lib/storage'
import { useDraftChannel } from './useDraftChannel'
import { useQueue } from './useQueue'
import type { QueueControls } from './useQueue'
import type { DraftState, PlayerSummary, PlayersResponse } from '../types/protocol'

interface DraftContextValue extends QueueControls {
  draft: DraftState | null
  players: PlayerSummary[]
  loading: boolean
  error: string | null
  poolLoading: boolean
  teamToken: string | null
  refetchPool: () => Promise<void>
}

const DraftContext = createContext<DraftContextValue | null>(null)

export function useDraftContext(): DraftContextValue {
  const ctx = useContext(DraftContext)
  if (!ctx) throw new Error('useDraftContext must be used inside DraftProvider')
  return ctx
}

interface DraftProviderProps {
  code: string
  children: ReactNode
}

export function DraftProvider({ code, children }: DraftProviderProps) {
  const joinCode = code.toUpperCase()
  const teamToken = getTeamToken(joinCode)

  // Player pool
  const [players, setPlayers] = useState<PlayerSummary[]>([])
  const [poolLoading, setPoolLoading] = useState(true)

  const fetchPool = useCallback(async () => {
    try {
      setPoolLoading(true)
      const response = await api<PlayersResponse>(`/drafts/${joinCode}/players`)
      setPlayers(response.players)
    } catch {
      // Pool fetch failure is non-fatal — the draft still works.
    } finally {
      setPoolLoading(false)
    }
  }, [joinCode])

  // Initial pool fetch
  useEffect(() => {
    void fetchPool()
  }, [fetchPool])

  // Draft state via cable
  const { draft, loading, error } = useDraftChannel({
    code: joinCode,
    teamToken,
    onRankingsImported: fetchPool,
  })

  const queueControls = useQueue(joinCode, teamToken, draft?.my_queue)

  const value: DraftContextValue = {
    draft,
    players,
    loading,
    error,
    poolLoading,
    teamToken,
    refetchPool: fetchPool,
    ...queueControls,
  }

  return <DraftContext value={value}>{children}</DraftContext>
}
